// ============= Imports =============
// External dependencies
import { useMemo } from 'react';

// Internal dependencies - Types
import {
  FormHandlers,
  SharePointUpdatePayload,
  FormState,
  SharePointApiResponse,
  SharePointUserProfile,
  TWWorksForInfo,
} from '../types';

// Internal dependencies - Config & APIs
import { API_CONFIG } from '../../../../../config';
import { fetchSharePointUser } from '../../../../apis/sharePoint';
import { AuthContextProps } from '@/contexts/AuthContext';
import { FormDataType, ErrorsType } from '@tw/tw-component-library';
import { useErrorTracker } from '../../../../contexts/ErrorContext';

// ============= Types =============
interface FormHandlersProps {
  getAccessToken: AuthContextProps['getAccessToken'];
  email: string;
  handleFloorChange: (value: string) => void;
  onSuccess: () => void;
  setState: (fn: (prev: FormState) => FormState) => void;
  pendingRemovals: string[];
  setPendingRemovals: React.Dispatch<React.SetStateAction<string[]>>;
  formData: FormState;
  fetchSharePointUser: (props: {
    getAccessToken: AuthContextProps['getAccessToken'];
    searchValue: string;
  }) => Promise<SharePointApiResponse>;
}

// ============= Constants =============
const SHAREPOINT_SCOPE =
  'api://24f835fd-ad36-4d11-a440-3cb3a93a6bcb/access_as_user';
const PA_KEYS = ['twLegalPA', 'twLegalPA2', 'twLegalPA3'] as const;
const WORKS_FOR_PREFIX = 'twWorksfor';
const MAX_WORKS_FOR_FIELDS = 20;

// ============= Hook Implementation =============
export const useFormHandlers = ({
  getAccessToken,
  email,
  handleFloorChange,
  onSuccess,
  setState,
  pendingRemovals,
  setPendingRemovals,
  formData,
}: FormHandlersProps): FormHandlers => {
  const { trackComponentError } = useErrorTracker();

  /**
   * Updates WorksFor fields for a PA
   */
  const updateWorksForFields = async (
    token: string,
    paEmail: string,
    action: 'add' | 'remove'
  ): Promise<void> => {
    try {
      if (action === 'add') {
        const worksForFields = await fetchWorksForFields(paEmail);
        const emptySlot = worksForFields.find((field) => !field.value);

        if (!emptySlot) {
          const error = new Error('No empty slots available for WorksFor');
          trackComponentError(error, 'updateWorksForFields', {
            paEmail,
            action,
            availableSlots: worksForFields.length,
          });
          throw error;
        }

        await createSharePointRequest(token, {
          accountName: paEmail,
          propertyName: emptySlot.fieldName,
          propertyValue: email,
        });
      } else {
        const worksForFields = await fetchWorksForFields(paEmail);
        const updatePromises = worksForFields
          .filter((field) => field.value === `i:0#.f|membership|${email}`)
          .map((field) =>
            createSharePointRequest(token, {
              accountName: paEmail,
              propertyName: field.fieldName,
              propertyValue: null,
            })
          );

        await Promise.all(updatePromises);
      }
    } catch (error) {
      trackComponentError(
        error instanceof Error ? error : new Error(String(error)),
        'updateWorksForFields',
        { paEmail, action }
      );
      throw error;
    }
  };

  /**
   * Fetches all WorksFor fields for a PA
   */
  const fetchWorksForFields = async (paEmail: string) => {
    try {
      const response = await fetchSharePointUser({
        getAccessToken,
        searchValue: paEmail,
      });

      if (!response.success || !response.data) {
        const error = new Error('Failed to fetch WorksFor fields');
        trackComponentError(error, 'fetchWorksForFields', {
          paEmail,
          responseSuccess: response.success,
          hasData: !!response.data,
        });
        throw error;
      }

      const worksForFields = [];
      const data = response.data as SharePointUserProfile;

      for (let i = 1; i <= MAX_WORKS_FOR_FIELDS; i++) {
        const fieldName = `${WORKS_FOR_PREFIX}${i}`;
        const value = data[fieldName as keyof TWWorksForInfo] || null;
        worksForFields.push({ fieldName, value });
      }

      return worksForFields;
    } catch (error) {
      trackComponentError(
        error instanceof Error ? error : new Error(String(error)),
        'fetchWorksForFields',
        { paEmail }
      );
      throw error;
    }
  };

  /**
   * Handles PA updates including additions and removals
   */
  const handlePAUpdates = async (
    token: string,
    email: string,
    newPAValues: string[]
  ): Promise<void> => {
    try {
      const validRemovals = pendingRemovals.filter(
        (email) => email.trim().length > 0
      );

      if (validRemovals.length > 0) {
        for (const paEmail of validRemovals) {
          try {
            await updateWorksForFields(token, paEmail, 'remove');
          } catch (error) {
            trackComponentError(
              error instanceof Error ? error : new Error(String(error)),
              'handlePAUpdates-removal',
              { paEmail, email }
            );
            // Continue with other removals even if one fails
          }
        }
        setPendingRemovals([]);
      }

      const validNewPAs = newPAValues.filter((pa) => pa.trim().length > 0);

      for (const paEmail of validNewPAs) {
        try {
          await updateWorksForFields(token, paEmail, 'add');
        } catch (error) {
          trackComponentError(
            error instanceof Error ? error : new Error(String(error)),
            'handlePAUpdates-addition',
            { paEmail, email }
          );
          throw error;
        }
      }

      // Clear existing PA values
      await Promise.all(
        PA_KEYS.map((key) =>
          createSharePointRequest(token, {
            accountName: email,
            propertyName: key,
            propertyValue: null,
          })
        )
      );

      // Set new PA values
      if (validNewPAs.length > 0) {
        await Promise.all(
          validNewPAs.map((value, index) => {
            if (index < PA_KEYS.length) {
              return createSharePointRequest(token, {
                accountName: email,
                propertyName: PA_KEYS[index],
                propertyValue: value,
              });
            }
            return Promise.resolve();
          })
        );
      }
    } catch (error) {
      trackComponentError(
        error instanceof Error ? error : new Error(String(error)),
        'handlePAUpdates',
        {
          email,
          newPACount: newPAValues.length,
          removalCount: pendingRemovals.length,
        }
      );
      throw error;
    }
  };

  /**
   * Handles floor updates in SharePoint
   */
  const handleFloorUpdates = async (
    token: string,
    email: string,
    floorRoom: string
  ): Promise<void> => {
    try {
      const response = await createSharePointRequest(token, {
        accountName: email,
        propertyName: 'twRoomFloor',
        propertyValue: floorRoom || null,
      });

      if (!response.ok) {
        const error = new Error(
          `Failed to update floor: ${response.status} ${response.statusText}`
        );
        trackComponentError(error, 'handleFloorUpdates', {
          email,
          floorRoom,
          status: response.status,
          statusText: response.statusText,
        });
        throw error;
      }
    } catch (error) {
      trackComponentError(
        error instanceof Error ? error : new Error(String(error)),
        'handleFloorUpdates',
        { email, floorRoom }
      );
      throw error;
    }
  };

  /**
   * Creates a SharePoint request
   */
  const createSharePointRequest = async (
    token: string,
    payload: SharePointUpdatePayload
  ): Promise<Response> => {
    try {
      const baseUrl = import.meta.env.MODE as 'development' | 'production';

      const response = await fetch(
        `${API_CONFIG[baseUrl].baseUrl}/Sharepoint/SetSingleValueProfileProperty`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const error = new Error(
          `SharePoint request failed: ${response.status} ${response.statusText}`
        );
        trackComponentError(error, 'createSharePointRequest', {
          payload,
          status: response.status,
          statusText: response.statusText,
        });
        throw error;
      }

      return response;
    } catch (error) {
      trackComponentError(
        error instanceof Error ? error : new Error(String(error)),
        'createSharePointRequest',
        { payload }
      );
      throw error;
    }
  };

  return useMemo(
    () => ({
      onSubmit: async (data: FormDataType) => {
        setState((prev) => ({ ...prev, isLoading: true }));

        try {
          const token = await getAccessToken([SHAREPOINT_SCOPE], {
            skipStorageToken: true,
          });

          if (!token) {
            const error = new Error('Failed to acquire SharePoint token');
            trackComponentError(error, 'onSubmit-tokenAcquisition');
            throw error;
          }

          if (!email) {
            const error = new Error('No email selected for the user');
            trackComponentError(error, 'onSubmit-validation', {
              hasEmail: !!email,
            });
            throw error;
          }

          if ('twlegalPA' in data) {
            await handlePAUpdates(token, email, data.twlegalPA);
          }

          if ('floorRoom' in data) {
            await handleFloorUpdates(token, email, data.floorRoom);
          }

          onSuccess?.();
          return true;
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error';

          trackComponentError(
            error instanceof Error ? error : new Error(String(error)),
            'onSubmit',
            { email, formData: data }
          );

          setState((prev) => ({
            ...prev,
            error: `Error updating profile: ${errorMessage}`,
          }));
          throw error;
        } finally {
          setState((prev) => ({ ...prev, isLoading: false }));
        }
      },
      onChange: (data: FormDataType) => {
        if ('twlegalPA' in data) {
          console.log('Form changed - PA values:', data.twlegalPA);
        }

        if ('floorRoom' in data) {
          handleFloorChange(data.floorRoom);
          console.log('Form changed - Floor values:', data.floorRoom);
        }
      },
      onError: (errors: ErrorsType) => {
        trackComponentError(
          new Error('Form validation errors occurred'),
          'onError',
          { errors }
        );
      },
    }),
    [
      getAccessToken,
      email,
      handleFloorChange,
      onSuccess,
      setState,
      pendingRemovals,
      formData,
      trackComponentError,
    ]
  );
};
